import React, { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Alert } from "../components/Alert";
import { useAuth } from "../contexts/useAuth";

interface Message {
  text: string;
  type: 'error' | 'success';
}

interface FieldErrors {
  [key: string]: boolean;
}

interface LoginLocationState {
  email?: string;
  message?: string;
}

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const alertRef = useRef<HTMLDivElement>(null);
  const { login } = useAuth();
  const locationState = location.state as LoginLocationState | null;

  const [email, setEmail] = useState<string>(locationState?.email ?? "");
  const [password, setPassword] = useState<string>("");
  const [remember, setRemember] = useState<boolean>(false);
  const [message, setMessage] = useState<Message>({
    text: locationState?.message ?? "",
    type: locationState?.message ? "success" : "error",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loginSuccessful, setLoginSuccessful] = useState<boolean>(false);
  const [showResendActivation, setShowResendActivation] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const clearFieldError = (fieldName: string): void => {
    if (fieldErrors[fieldName]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
    clearFieldError("email");
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPassword(e.target.value);
    clearFieldError("password");
  };

  const validateForm = (): FieldErrors => {
    const errors: FieldErrors = {};

    if (!email.trim()) {
      errors.email = true;
    }

    if (!password.trim()) {
      errors.password = true;
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ text: "", type: "error" });
    setShowResendActivation(false);
    setFieldErrors({});

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setMessage({ text: "Please fill in all required fields", type: "error" });
      setIsLoading(false);
      return;
    }

    try {
      const result = await login(email, password, remember);

      if (!result.success) {
        const errorMessage = 'error' in result && typeof result.error === "string"
          ? result.error
          : "Login failed. Please try again.";
        setMessage({ text: errorMessage, type: "error" });
        if (errorMessage.includes("Email not activated")) {
          setShowResendActivation(true);
        }
        setIsLoading(false);
        return;
      }

      const successMessage = 'message' in result && result.message
        ? result.message
        : "Login successful";
      setMessage({ text: successMessage, type: "success" });
      setLoginSuccessful(true);
      setTimeout(() => {
        navigate("/dashboard");
      }, 500);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Network error. Please try again.";
      setMessage({ text: message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendActivation = (): void => {
    navigate("/resend-activation", { state: { email } });
  };

  const getInputClass = (fieldName: string): string => {
    return `w-full input ${fieldErrors[fieldName] ? "input-error" : ""}`;
  };

  return (
    <div className="login-layout">
      <div className="lcont">
        <div ref={alertRef}>
          {message.text && <Alert type={message.type} message={message.text} />}
        </div>

        {showResendActivation && (
          <div className="alert alert-info">
            <div className="flex flex-col">
              <span>Need to activate your account?</span>
              <button
                onClick={handleResendActivation}
                className="btn btn-sm btn-primary mt-2"
              >
                Resend Activation Email
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Email</legend>
            <input
              type="email"
              placeholder="you@researchcentre.org"
              className={getInputClass("email")}
              value={email}
              onChange={handleEmailChange}
              aria-describedby="email-help"
              autoComplete="email"
            />
          </fieldset>

          <fieldset className="fieldset">
            <legend className="fieldset-legend">Password</legend>
            <input
              type="password"
              placeholder="••••••••"
              className={getInputClass("password")}
              value={password}
              onChange={handlePasswordChange}
            />
          </fieldset>

          <div className="flex justify-between text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={remember}
                onChange={() => setRemember(!remember)}
              />
              Remember me
            </label>
            <Link
              to="/forgot-password"
              className="text-primary hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          <button
            type="submit"
            className="w-full btn btn-primary"
            disabled={isLoading || loginSuccessful}
          >
            {isLoading ? (
              <span className="loading loading-spinner"></span>
            ) : (
              "Login"
            )}
          </button>
        </form>

        <div className="text-center space-y-2">
          <p className="text-sm">
            Don't have an account?{" "}
            <Link to="/register" className="text-primary hover:underline">
              Register
            </Link>
          </p>
          <div className="divider text-xs">OR</div>
          <p className="text-sm">
            Need to register an organisation?{" "}
            <Link
              to="/register-organisation"
              className="text-primary hover:underline font-medium"
            >
              Register Organisation
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
